import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { DEFAULT_BUSINESS_TRAVEL_ENTRY } from "@/contexts/wizard/useScope3State";
import { calculateBusinessTravelEmissions } from "@/service/emissions";
import type { BusinessTravelEntry } from "@/contexts/wizard/types";

const modeLabelKeys = {
  flight_domestic: "Flight - Domestic",
  flight_short_haul_intl: "Flight - Short-haul",
  flight_long_haul_intl: "Flight - Long-haul",
  train_national: "Train - National",
  train_international: "Train - International",
  bus: "Bus/Coach",
  taxi: "Taxi",
  car_rental_petrol: "Rental Car - Petrol",
  car_rental_diesel: "Rental Car - Diesel",
} as const;

interface Props {
  isOpen: boolean;
}

export default function BusinessTravelFormSection({ isOpen }: Props) {
  const {
    scope3Data,
    assessment,
    currentBusinessTravelEntry,
    setCurrentBusinessTravelEntry,
    addBusinessTravelEntry,
    updateBusinessTravelEntry,
    deleteBusinessTravelEntry,
    forceSave,
    isLocked,
    isReadOnly,
  } = useWizard();
  const { toast } = useToast();
  const disabled = isLocked || isReadOnly;
  const pendingSaveRef = useRef(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCurrentBusinessTravelEntry(DEFAULT_BUSINESS_TRAVEL_ENTRY);
      setEditingEntryId(null);
    }
  }, [isOpen]);
  const getModeLabel = (mode: string) => {
    return modeLabelKeys[mode as keyof typeof modeLabelKeys] ?? mode;
  };

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      forceSave();
    }
  }, [scope3Data.businessTravelEntries, forceSave]);

  const totalTco2e = useMemo(() => {
    return scope3Data.businessTravelEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0);
  }, [scope3Data.businessTravelEntries]);

  const completion = scope3Data.businessTravelEntries.length > 0 ? 100 : 0;

  const handleEditEntry = (entry: BusinessTravelEntry) => {
    setCurrentBusinessTravelEntry({
      mode: entry.mode,
      distanceKm: entry.distanceKm,
      period: entry.period,
      description: entry.description,
    });
    setEditingEntryId(entry.id);
  };

  const handleCancelEdit = () => {
    setCurrentBusinessTravelEntry(DEFAULT_BUSINESS_TRAVEL_ENTRY);
    setEditingEntryId(null);
  };

  const handleUpdateEntry = async () => {
    const id = editingEntryId!;
    updateBusinessTravelEntry(id, { ...currentBusinessTravelEntry, calculating: true });
    try {
      const tco2e = await calculateBusinessTravelEmissions(currentBusinessTravelEntry);
      updateBusinessTravelEntry(id, { tco2e, calculating: false });
      pendingSaveRef.current = true;
      setCurrentBusinessTravelEntry(DEFAULT_BUSINESS_TRAVEL_ENTRY);
      setEditingEntryId(null);
      toast({
        title: "Entry updated",
        description: tco2e != null ? `${tco2e.toFixed(3)} tCO₂e` : undefined,
      });
    } catch {
      updateBusinessTravelEntry(id, { calculating: false });
      toast({ title: "Failed to update entry", variant: "destructive" });
    }
  };

  const handleAddEntry = async () => {
    const result = await addBusinessTravelEntry();
    if (!result.ok) {
      toast({
        title: result.error || "Unable to add travel entry",
        variant: "destructive",
      });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Business travel entry added",
      description: result.tco2e
        ? `${result.tco2e.toFixed(3)} tCO2e`
        : "Calculating...",
    });
  };

  return (
    <div className="space-y-6">
      {disabled && <LockedBanner />}

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">{"Add Travel Entry"}</h4>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">{"Transport mode"}</Label>
              <Select
                value={currentBusinessTravelEntry.mode}
                onValueChange={(value) =>
                  setCurrentBusinessTravelEntry((prev) => ({
                    ...prev,
                    mode: value as typeof currentBusinessTravelEntry.mode,
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger className="form-select">
                  <SelectValue placeholder={"Select mode"} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(modeLabelKeys).map(([value]) => (
                    <SelectItem key={value} value={value}>
                      {getModeLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Distance (km)"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="0.1"
                value={currentBusinessTravelEntry.distanceKm ?? ""}
                onChange={(e) =>
                  setCurrentBusinessTravelEntry((prev) => ({
                    ...prev,
                    distanceKm: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 450"}
                disabled={disabled}
              />
            </div>
            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentBusinessTravelEntry.period ?? null}
              onChange={(value) => setCurrentBusinessTravelEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <Label className="form-label">{"Route/description (optional)"}</Label>
            <Input
              className="form-input"
              value={currentBusinessTravelEntry.description || ""}
              onChange={(e) =>
                setCurrentBusinessTravelEntry((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder={"e.g. London to Berlin conference"}
              disabled={disabled}
            />
          </div>

          <div>
            {editingEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={handleAddEntry} disabled={disabled} className="form-add-button">
                <Plus className="mr-2 h-3 w-3" /> {"Add Entry"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-header">
          <h4 className="entries-title">{"Entries"}</h4>
          <span className="entries-count">
            {`${scope3Data.businessTravelEntries.length} entries`}
          </span>
        </div>

        {scope3Data.businessTravelEntries.length === 0 ? (
          <p className="entries-empty">{"No business travel entries yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-row-base">
                <TableHead className="table-head-base">{"Mode"}</TableHead>
                <TableHead className="table-head-base text-center">
                  {"Distance"}
                </TableHead>
                <TableHead className="table-head-base text-center">{"Period"}</TableHead>
                <TableHead className="table-head-base text-center">{"tCO2e"}</TableHead>
                <TableHead className="table-head-base text-right">{"Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scope3Data.businessTravelEntries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className={`table-row-base${editingEntryId === entry.id ? " outline outline-2 outline-primary/30" : ""}`}
                >
                  <TableCell className="table-cell-base-strong">{getModeLabel(entry.mode)}</TableCell>
                  <TableCell className="table-cell-base-center">{`${entry.distanceKm.toFixed(1)} km`}</TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.calculating ? "Calculating..." : (entry.tco2e ?? 0).toFixed(3)}
                  </TableCell>
                  <TableCell className="table-cell-base-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingEntryId !== null && editingEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteBusinessTravelEntry(entry.id);
                        pendingSaveRef.current = true;
                      }}
                      className="delete-button"
                      disabled={disabled || editingEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="total-card">
        <p className="total-label">{"Scope 3 Business Travel Total"}</p>
        <p className="total-value">{`${totalTco2e.toFixed(3)} tCO2e`}</p>
      </div>

      <p className="text-xs leading-4 text-slate-500">{`Completion: ${completion}%`}</p>
    </div>
  );
}
